import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Row, Col } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  RefreshCw,
  Dumbbell,
  BookOpen,
  AlertTriangle,
  Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getDashboardOverview } from '../../services/dashboardService';
import { useTheme } from '../../components/Theme/ThemeContext';
import './Dashboard.scss';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const LIBRARY_STATUS_LABELS = {
  Active: 'Aktif',
  Confirmed: 'Kullanıcı Onaylı',
  StaffConfirmed: 'Personel Onaylı',
  Pending: 'Bekliyor',
  CheckedIn: 'Giriş Yapıldı',
  Completed: 'Tamamlandı',
  Cancelled: 'İptal',
  Expired: 'Süresi Doldu',
  Unknown: 'Belirsiz'
};

const LIBRARY_STATUS_TONE = {
  Active: 'primary',
  Confirmed: 'primary',
  StaffConfirmed: 'success',
  Pending: 'warning',
  CheckedIn: 'success',
  Completed: 'success',
  Cancelled: 'danger',
  Expired: 'muted'
};

const formatNumber = (num) => (Number(num) || 0).toLocaleString('tr-TR');
const formatCurrency = (num) =>
  `${(Number(num) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;

const Skeleton = () => <span className="arc-skeleton-val" />;
const Unavailable = () => <span className="arc-kpi-unavailable">—</span>;

const getServiceIssue = (result) => {
  if (!result || (result.ok && !result.partial && !result.value?.hourlyError)) return null;
  return result.value?.hourlyError || result.partialReason || result.reason
    || result.error?.response?.data?.resultMessage || result.error?.message
    || (result.partial ? 'Bazı veriler alınamadı; servis ayrıntılı bir neden bildirmedi.' : 'Servisten yanıt alınamadı.');
};

const StatusBar = ({ label, value, total, tone }) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="arc-status-row">
      <div className="arc-status-row-top">
        <span className="arc-status-label">
          <span className={`arc-status-dot ${tone}`} />
          {label}
        </span>
        <span className="arc-status-value">
          {formatNumber(value)}
          <span className="arc-status-pct">%{pct.toFixed(0)}</span>
        </span>
      </div>
      <div className="arc-status-track">
        <div className={`arc-status-fill ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [selectedRange, setSelectedRange] = useState(1);
  const [visibleSeries, setVisibleSeries] = useState({ sports: true, library: true, revenue: true });
  const allSeriesVisible = Object.values(visibleSeries).every(Boolean);
  const anySeriesVisible = Object.values(visibleSeries).some(Boolean);
  const toggleSeries = (key) => setVisibleSeries((current) => {
    if (key !== 'all') return { ...current, [key]: !current[key] };
    const next = !Object.values(current).every(Boolean);
    return { sports: next, library: next, revenue: next };
  });
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const requestId = useRef(0);

  const fetchOverview = useCallback(async (days) => {
    const id = ++requestId.current;
    setLoading(true);
    setOverview(null);
    try {
      await getDashboardOverview(days, (data) => {
        if (id === requestId.current) setOverview(data);
      });
    } catch (err) {
      console.error('Dashboard verileri alınamadı:', err);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview(selectedRange);
    return () => { requestId.current += 1; };
  }, [fetchOverview, selectedRange]);

  const isDark = theme === 'dark';
  const gridColor = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.06)';
  const textColor = isDark ? '#94a3b8' : '#64748b';

  const sports = overview?.sports?.ok ? overview.sports.value : null;
  const library = overview?.library?.ok ? overview.library.value : null;
  const revenue = overview?.revenue?.ok ? overview.revenue.value : null;
  const incidents = overview?.incidents?.ok ? overview.incidents.value : null;
  const dailyRevenue = overview?.revenueDaily?.ok ? overview.revenueDaily.value : null;
  const isHourly = selectedRange === 1;
  const sportsPending = loading && !overview?.sports;
  const libraryPending = loading && !overview?.library;
  const revenuePending = loading && !overview?.revenue;
  const incidentsPending = loading && !overview?.incidents;
  const dailyRevenuePending = loading && !overview?.revenueDaily;
  const chartPending = (visibleSeries.sports && (sportsPending || (isHourly && sports?.hourlyPending)))
    || (visibleSeries.library && (libraryPending || (isHourly && library?.hourlyPending)))
    || (visibleSeries.revenue && dailyRevenuePending);
  const rangeLabel = selectedRange === 1 ? 'Bugün' : `Son ${selectedRange} gün`;
  const hourlyErrors = isHourly ? [
    visibleSeries.sports && sports?.hourlyError && 'Spor randevularının saatlik dağılımı alınamadı.',
    visibleSeries.library && library?.hourlyError && 'Kütüphane randevularının saatlik dağılımı alınamadı.'
  ].filter(Boolean) : [];

  const chartData = useMemo(() => {
    const keys = isHourly
      ? Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, '0')}:00`)
      : overview?.range?.keys || [];
    const labels = isHourly ? keys : keys.map((k) =>
      new Date(`${k}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })
    );
    const datasets = [];
    const dense = keys.length > 20;

    const sportsSeries = isHourly ? sports?.hourly : sports?.daily;
    const librarySeries = isHourly ? library?.hourly : library?.daily;

    if (sportsSeries && visibleSeries.sports) {
      datasets.push({
        label: 'Spor Randevuları',
        data: keys.map((k) => sportsSeries[k] || 0),
        borderColor: '#f43f5e',
        backgroundColor: isDark ? 'rgba(244, 63, 94, 0.10)' : 'rgba(244, 63, 94, 0.07)',
        borderWidth: 2,
        fill: true,
        tension: 0.35,
        pointRadius: dense ? 1 : 3,
        pointHoverRadius: 5,
        pointBackgroundColor: '#f43f5e'
      });
    }

    if (librarySeries && visibleSeries.library) {
      datasets.push({
        label: 'Kütüphane Randevuları',
        data: keys.map((k) => librarySeries[k] || 0),
        borderColor: '#f59e0b',
        backgroundColor: isDark ? 'rgba(245, 158, 11, 0.10)' : 'rgba(245, 158, 11, 0.07)',
        borderWidth: 2,
        fill: true,
        tension: 0.35,
        pointRadius: dense ? 1 : 3,
        pointHoverRadius: 5,
        pointBackgroundColor: '#f59e0b'
      });
    }

    if (dailyRevenue && visibleSeries.revenue) {
      datasets.push({
        label: 'Gelir (₺)',
        data: keys.map((k) => Math.round((dailyRevenue[k] || 0) * 100) / 100),
        borderColor: '#10b981',
        backgroundColor: isDark ? 'rgba(16, 185, 129, 0.10)' : 'rgba(16, 185, 129, 0.07)',
        borderWidth: 2,
        borderDash: visibleSeries.sports || visibleSeries.library ? [5, 4] : [],
        fill: !visibleSeries.sports && !visibleSeries.library,
        tension: 0.35,
        pointRadius: dense ? 1 : 3,
        pointHoverRadius: 5,
        pointBackgroundColor: '#10b981',
        yAxisID: 'revenue'
      });
    }

    return { labels, datasets };
  }, [overview, sports, library, dailyRevenue, visibleSeries, isDark, isHourly]);

  const showCountAxis = chartData.datasets.some((dataset) => dataset.yAxisID !== 'revenue');
  const showRevenueAxis = Boolean(dailyRevenue) && visibleSeries.revenue;

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          display: true,
          position: 'top',
          align: 'end',
          labels: {
            boxWidth: 8,
            boxHeight: 8,
            usePointStyle: true,
            pointStyle: 'circle',
            color: textColor,
            font: { family: 'Inter', size: 12, weight: 500 },
            padding: 16
          }
        },
        tooltip: {
          backgroundColor: isDark ? 'rgba(10, 10, 11, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          titleColor: isDark ? '#f8fafc' : '#0f172a',
          bodyColor: isDark ? '#cbd5e1' : '#334155',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.1)',
          borderWidth: 1,
          padding: 12,
          boxPadding: 6,
          usePointStyle: true,
          cornerRadius: 8,
          bodyFont: { family: 'Inter', size: 12 },
          titleFont: { family: 'Inter', size: 12, weight: 600 },
          callbacks: {
            label: (ctx) =>
              ctx.dataset.yAxisID === 'revenue'
                ? ` Gelir: ${formatCurrency(ctx.parsed.y)}`
                : ` ${ctx.dataset.label}: ${Number(ctx.parsed.y).toLocaleString('tr-TR')}`
          }
        }
      },
      scales: {
        x: {
          grid: { color: gridColor, drawBorder: false },
          ticks: { color: textColor, font: { family: 'Inter', size: 11 }, maxRotation: 0, autoSkipPadding: 12 }
        },
        y: {
          display: showCountAxis,
          beginAtZero: true,
          position: 'left',
          grid: { color: gridColor, drawBorder: false },
          ticks: { color: textColor, font: { family: 'Inter', size: 11 }, precision: 0 },
          title: { display: showCountAxis && showRevenueAxis, text: 'Randevu', color: textColor, font: { family: 'Inter', size: 11 } }
        },
        revenue: {
          display: showRevenueAxis,
          beginAtZero: true,
          position: 'right',
          grid: { drawOnChartArea: !showCountAxis, color: gridColor, drawBorder: false },
          ticks: {
            color: textColor,
            font: { family: 'Inter', size: 11 },
            callback: (v) => `${Number(v).toLocaleString('tr-TR', { notation: 'compact', maximumFractionDigits: 1 })} ₺`
          }
        }
      }
    }),
    [isDark, gridColor, textColor, showCountAxis, showRevenueAxis]
  );

  const sportStatusTotal = sports
    ? sports.status.approved + sports.status.failed + sports.status.cancelled + sports.status.refunded
    : 0;

  const libraryStatusEntries = library
    ? Object.entries(library.status).sort((a, b) => b[1] - a[1])
    : [];
  const libraryStatusTotal = libraryStatusEntries.reduce((sum, [, v]) => sum + v, 0);

  const successRate =
    revenue && revenue.totalTransactions > 0
      ? ((revenue.successfulTransactions / revenue.totalTransactions) * 100).toFixed(1)
      : null;

  const services = overview
    ? [
        { name: 'Spor Rezervasyon Servisi', detail: 'Tesis randevuları', result: overview.sports },
        { name: 'Kütüphane Randevu Servisi', detail: 'Salon & masa rezervasyonları', result: overview.library },
        { name: 'Ödeme & Gelir Servisi', detail: 'Sanal POS işlemleri', result: overview.revenue },
        { name: isHourly ? 'Saatlik Gelir Grafiği' : 'Günlük Gelir Grafiği', detail: 'Ödeme hareketlerinin tüm sayfaları', result: overview.revenueDaily },
        { name: 'Bize Bildir Servisi', detail: 'Vatandaş talepleri', result: overview.incidents }
      ]
    : [];

  const allHealthy = services.length > 0 && services.every((s) => s.result?.ok && !s.result.partial && !s.result.value?.hourlyError);
  const slowestService = services.filter((s) => s.result).reduce(
    (slowest, service) => !slowest || service.result.ms > slowest.result.ms ? service : slowest,
    null
  );

  return (
    <div className="arc-dashboard">
      {/* Page Header */}
      <div className="arc-dashboard-header">
        <div className="arc-header-title-wrap">
          <div className="arc-title-row">
            <h1 className="arc-page-title">Genel Bakış &amp; Metrikler</h1>
          </div>
          <p className="arc-page-subtitle">
            İskenderun Belediyesi randevu, gelir ve talep göstergeleri
            {overview?.fetchedAt && (
              <span className="arc-last-updated">
                <Clock size={12} />
                Son güncelleme {overview.fetchedAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </p>
        </div>

        <div className="arc-header-actions">
          <div className="arc-segmented">
            {[1, 7, 30, 90].map((d) => (
              <button
                key={d}
                id={`dashboard-range-${d}`}
                type="button"
                className={`arc-segmented-item ${selectedRange === d ? 'active' : ''}`}
                onClick={() => setSelectedRange(d)}
              >
                {d === 1 ? 'Bugün' : `${d} Gün`}
              </button>
            ))}
          </div>

          <button
            id="dashboard-refresh"
            type="button"
            className="arc-btn arc-btn-secondary arc-btn-sm"
            onClick={() => fetchOverview(selectedRange)}
            disabled={loading}
            title="Verileri Yenile"
          >
            <RefreshCw size={14} className={loading ? 'arc-spin' : ''} />
            <span>Yenile</span>
          </button>
        </div>
      </div>

      {/* Primary KPI Cards */}
      <Row className="g-3 mb-4">
        <Col xl={3} sm={6}>
          <div className="arc-card arc-kpi-card arc-kpi-clickable" onClick={() => navigate('/sports-appointments')}>
            <div className="arc-kpi-body">
              <div className="arc-kpi-top">
                <span className="arc-kpi-label">Spor Randevuları</span>
              </div>
              <div className="arc-kpi-value">
                {sportsPending ? <Skeleton /> : sports ? formatNumber(sports.rangeCount) : <Unavailable />}
              </div>
              <div className="arc-kpi-footer">
                {sports ? (
                  <>
                    <span className="arc-badge arc-badge-primary">Bugün {formatNumber(sports.todayCount)} oluşturuldu</span>
                    <span className="arc-kpi-note">{formatNumber(sports.upcoming)} ileri tarihli</span>
                  </>
                ) : (
                  <span className="arc-kpi-note">{rangeLabel}</span>
                )}
              </div>
            </div>
          </div>
        </Col>

        <Col xl={3} sm={6}>
          <div className="arc-card arc-kpi-card arc-kpi-clickable" onClick={() => navigate('/library-appointments')}>
            <div className="arc-kpi-body">
              <div className="arc-kpi-top">
                <span className="arc-kpi-label">Kütüphane Randevuları</span>
              </div>
              <div className="arc-kpi-value">
                {libraryPending ? <Skeleton /> : library ? formatNumber(library.rangeCount) : <Unavailable />}
              </div>
              <div className="arc-kpi-footer">
                {library ? (
                  <>
                    <span className="arc-badge arc-badge-warning">Bugün {formatNumber(library.todayCount)} oluşturuldu</span>
                    <span className="arc-kpi-note">{library.libraries.length} kütüphane</span>
                  </>
                ) : (
                  <span className="arc-kpi-note">{rangeLabel}</span>
                )}
              </div>
            </div>
          </div>
        </Col>

        <Col xl={3} sm={6}>
          <div className="arc-card arc-kpi-card arc-kpi-clickable" onClick={() => navigate('/sports-appointments')}>
            <div className="arc-kpi-body">
              <div className="arc-kpi-top">
                <span className="arc-kpi-label">Tesis Gelirleri</span>
              </div>
              <div className="arc-kpi-value arc-kpi-value-currency">
                {revenuePending ? <Skeleton /> : revenue ? formatCurrency(revenue.totalRevenue) : <Unavailable />}
              </div>
              <div className="arc-kpi-footer">
                {revenue ? (
                  <>
                    <span className="arc-badge arc-badge-success">Bugün {formatCurrency(revenue.todayRevenue)}</span>
                    {successRate !== null && <span className="arc-kpi-note">%{successRate} başarılı ödeme</span>}
                  </>
                ) : (
                  <span className="arc-kpi-note">{rangeLabel}</span>
                )}
              </div>
            </div>
          </div>
        </Col>

        <Col xl={3} sm={6}>
          <div className="arc-card arc-kpi-card arc-kpi-clickable" onClick={() => navigate('/report-incidents')}>
            <div className="arc-kpi-body">
              <div className="arc-kpi-top">
                <span className="arc-kpi-label">Bize Bildir Talepleri</span>
              </div>
              <div className="arc-kpi-value">
                {incidentsPending ? <Skeleton /> : incidents ? formatNumber(incidents.total) : <Unavailable />}
              </div>
              <div className="arc-kpi-footer">
                {incidents ? (
                  <>
                    <span className="arc-badge arc-badge-danger">{formatNumber(incidents.byStatus.New)} yeni</span>
                    <span className="arc-kpi-note">{formatNumber(incidents.byStatus.InProgress)} işlemde</span>
                  </>
                ) : (
                  <span className="arc-kpi-note">tüm zamanlar</span>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Appointment Trend Chart */}
      <div className="arc-card arc-chart-block mb-4">
        <div className="arc-card-header">
          <div>
            <h3 className="arc-card-title">Randevu &amp; Gelir Trendi</h3>
            <p className="arc-card-subtitle">{isHourly ? 'Bugün oluşturulan randevuların saatlik dağılımı ve tesis gelirleri' : `Son ${selectedRange} günde oluşturulan randevular ve tesis gelirleri`}</p>
            {isHourly && <p className="arc-card-subtitle">Randevu oluşturulma ve ödeme işlem saatlerine göre · Türkiye saati</p>}
            {chartPending && <p className="arc-card-subtitle" role="status">Bekleyen seriler geldikçe grafik güncelleniyor…</p>}
            {hourlyErrors.length > 0 && <p className="arc-card-subtitle" role="status">{hourlyErrors.join(' ')} Verileri yenileyerek tekrar deneyebilirsiniz.</p>}
          </div>

          <div className="arc-series-toggles" role="group" aria-label="Grafikte gösterilen veriler">
            {[
              { key: 'all', label: 'Tümü' },
              { key: 'sports', label: 'Spor' },
              { key: 'library', label: 'Kütüphane' },
              { key: 'revenue', label: 'Gelir' }
            ].map((t) => (
              <button
                key={t.key}
                id={`dashboard-series-${t.key}`}
                type="button"
                className={`arc-btn arc-btn-secondary arc-btn-sm arc-series-toggle ${(t.key === 'all' ? allSeriesVisible : visibleSeries[t.key]) ? 'active' : ''}`}
                aria-pressed={t.key === 'all' ? allSeriesVisible : visibleSeries[t.key]}
                onClick={() => toggleSeries(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="arc-card-body">
          {!anySeriesVisible ? (
            <div className="arc-chart-canvas-wrap arc-chart-loading" role="status">
              Görüntülemek için bir veri seçin.
            </div>
          ) : chartPending && chartData.datasets.length === 0 ? (
            <div className="arc-chart-canvas-wrap arc-chart-loading">
              <RefreshCw size={22} className="arc-spin" />
            </div>
          ) : chartData.datasets.length === 0 ? (
            <div className="arc-chart-error">
              <AlertTriangle size={32} className="text-warning mb-2" />
              <h5>Veri alınamadı</h5>
              <p>Bu verileri görüntüleme yetkiniz olmayabilir veya servis yanıt vermiyor.</p>
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => fetchOverview(selectedRange)}>
                Yeniden Dene
              </button>
            </div>
          ) : (
            <div className="arc-chart-canvas-wrap">
              <Line data={chartData} options={chartOptions} />
            </div>
          )}
        </div>
      </div>

      {/* Status Breakdown + Live Service Health */}
      <Row className="g-3">
        <Col xs={12}>
          <div className="arc-card">
            <div className="arc-card-header">
              <div>
                <h3 className="arc-card-title">Randevu Durum Dağılımı</h3>
                <p className="arc-card-subtitle">Oluşturulma tarihine göre · {rangeLabel}</p>
              </div>
            </div>
            <div className="arc-card-body">
              <div className="arc-status-group">
                <div className="arc-status-group-title">
                  <Dumbbell size={14} /> Spor
                  <span className="arc-status-group-total">{sportsPending ? '…' : sports ? formatNumber(sportStatusTotal) : '—'}</span>
                </div>
                {sports ? (
                  <>
                    <StatusBar label="Onaylı / Ödendi" value={sports.status.approved} total={sportStatusTotal} tone="success" />
                    <StatusBar label="Ödeme Bekleyen / Başarısız" value={sports.status.failed} total={sportStatusTotal} tone="warning" />
                    <StatusBar label="İptal" value={sports.status.cancelled} total={sportStatusTotal} tone="danger" />
                    <StatusBar label="İade" value={sports.status.refunded} total={sportStatusTotal} tone="muted" />
                  </>
                ) : (
                  <p className="arc-status-empty">{sportsPending ? 'Yükleniyor…' : 'Veri alınamadı'}</p>
                )}
              </div>

              <div className="arc-status-group">
                <div className="arc-status-group-title">
                  <BookOpen size={14} /> Kütüphane
                  <span className="arc-status-group-total">{libraryPending ? '…' : library ? formatNumber(libraryStatusTotal) : '—'}</span>
                </div>
                {library && libraryStatusEntries.length > 0 ? (
                  libraryStatusEntries.map(([status, count]) => (
                    <StatusBar
                      key={status}
                      label={LIBRARY_STATUS_LABELS[status] || status}
                      value={count}
                      total={libraryStatusTotal}
                      tone={LIBRARY_STATUS_TONE[status] || 'primary'}
                    />
                  ))
                ) : (
                  <p className="arc-status-empty">
                    {libraryPending ? 'Yükleniyor…' : library ? 'Bu dönemde randevu yok' : 'Veri alınamadı'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Col>

        <Col xs={12}>
          <div className="arc-card">
            <div className="arc-card-header">
              <div>
                <h3 className="arc-card-title">Sistem Durumu &amp; Servisler</h3>
                <p className="arc-card-subtitle">Son yüklemede ölçülen servis yanıtları</p>
                {slowestService && (
                  <p className="arc-card-subtitle">
                    {loading ? 'Tamamlananlarda en uzun' : 'En uzun süren'}: {slowestService.name} · {formatNumber(slowestService.result.ms)} ms
                  </p>
                )}
              </div>
              {services.length > 0 && (
                <span className={`arc-badge ${allHealthy ? 'arc-badge-success' : 'arc-badge-warning'}`}>
                  <span className="arc-badge-dot" />
                  {loading ? 'Yükleniyor…' : allHealthy ? 'Tümü Aktif' : 'Kısmi Erişim'}
                </span>
              )}
            </div>
            <div className="arc-card-body p-0">
              <div className="arc-service-status-list">
                {services.length === 0 ? (
                  <div className="arc-service-item">
                    <span className="arc-service-details">Servisler kontrol ediliyor…</span>
                  </div>
                ) : (
                  services.map(({ name, detail, result }) => (
                    <div key={name} className="arc-service-item">
                      <div className="arc-service-info">
                        <span className="arc-service-name">{name}</span>
                        <span className="arc-service-details">
                          {detail}{result ? ` · ${formatNumber(result.ms)} ms` : ' · Bekleniyor…'}
                        </span>
                        {getServiceIssue(result) && (
                          <span className="arc-service-issue">Neden: {getServiceIssue(result)}</span>
                        )}
                      </div>
                      {!result ? (
                        <span className="arc-badge">Yükleniyor…</span>
                      ) : result.ok && !result.partial && !result.value?.hourlyError ? (
                        <span className="arc-badge arc-badge-success">Çalışıyor</span>
                      ) : result.partial || result.value?.hourlyError ? (
                        <span className="arc-badge arc-badge-warning">Kısmi</span>
                      ) : (
                        <span className="arc-badge arc-badge-danger">Erişilemedi</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
